import { useState, useEffect, Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ResourcesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title
    document.title = 'Resources & Insights - Revivacorp Business Turnaround Expertise';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Expert guidance, industry insights, and proven strategies to help your business navigate challenges and achieve sustainable growth. Business turnaround resources from Revivacorp.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/resources`);
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Resources & Insights - Revivacorp Business Turnaround Expertise' },
      { property: 'og:description', content: 'Expert guidance, industry insights, and proven strategies to help your business navigate challenges and achieve sustainable growth. Business turnaround resources from Revivacorp.' },
      { property: 'og:url', content: `${siteUrl}/resources` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: `${siteUrl}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Resources & Insights - Revivacorp Business Turnaround Expertise' },
      { name: 'twitter:description', content: 'Expert guidance, industry insights, and proven strategies to help your business navigate challenges and achieve sustainable growth. Business turnaround resources from Revivacorp.' },
      { name: 'twitter:image', content: `${siteUrl}/og-image.jpg` }
    ];
    
    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Resources - Business Turnaround Insights',
      description: 'Expert insights, guides, and articles on business turnaround, restructuring, and financial recovery strategies.',
      url: `${siteUrl}/resources`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'Article',
            position: 1,
            name: 'The Hidden Trap Killing Small Businesses: Commingling Personal & Business Finances',
            url: `${siteUrl}/resources/personal-business-finances`
          },
          {
            '@type': 'Article',
            position: 2,
            name: 'The Silent Killer: How Ignoring AI & Technology Is Putting Businesses Out of Business',
            url: `${siteUrl}/resources/tech-ai-falling-behind`
          }
        ]
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Resources',
          item: `${siteUrl}/resources`
        }
      ]
    };

    const collectionScript = document.createElement('script');
    collectionScript.type = 'application/ld+json';
    collectionScript.text = JSON.stringify(collectionSchema);
    document.head.appendChild(collectionScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(collectionScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  const categories = ['All', 'Business Strategy', 'Financial Management', 'Leadership', 'Turnaround', 'Case Studies'];

  const resources = [
    {
      id: 0,
      category: 'Financial Management',
      title: 'The Hidden Trap Killing Small Businesses: Commingling Personal & Business Finances',
      description: 'At Revivacorp, we see it constantly — struggling small business owners mixing personal and business accounts. Here\'s why we address both sides, and why it\'s critical to your recovery.',
      image: 'https://public.readdy.ai/ai/img_res/bce7d5a2e27a014208b4d83ed321ea16.jpg',
      date: 'July 14, 2025',
      readTime: '7 min read',
      link: '/resources/personal-business-finances',
      featured: true,
    },
    {
      id: 10,
      category: 'Business Strategy',
      title: 'The Silent Killer: How Ignoring AI & Technology Is Putting Businesses Out of Business',
      description: 'While you hesitate, your competitors are using AI and automation to cut costs, serve customers faster, and grow their margins. Here\'s what\'s really happening — and what you can do about it.',
      image: 'https://readdy.ai/api/search-image?query=dramatic%20split%20scene%20showing%20a%20modern%20business%20office%20with%20glowing%20AI%20screens%20robots%20and%20automation%20technology%20on%20one%20side%20thriving%20with%20growth%20charts%20while%20the%20other%20side%20shows%20a%20traditional%20outdated%20office%20with%20stressed%20workers%20falling%20behind%20competitors%20dark%20moody%20cinematic%20lighting%20with%20strong%20contrast&width=800&height=500&seq=tech-ai-card-001&orientation=landscape',
      date: 'July 21, 2025',
      readTime: '9 min read',
      link: '/resources/tech-ai-falling-behind',
      featured: true,
    },
    {
      id: 1,
      category: 'Business Strategy',
      title: '10 Signs Your Business Needs a Turnaround Specialist',
      description: 'Learn to identify the early warning signs that indicate your business may need professional intervention to avoid financial distress and operational challenges.',
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20consultant%20analyzing%20financial%20charts%20and%20graphs%20in%20modern%20office%20with%20laptop%20and%20documents%20showing%20business%20recovery%20metrics%20and%20strategic%20planning%20elements%20with%20clean%20minimalist%20background&width=800&height=500&seq=res1&orientation=landscape',
      date: 'March 15, 2024',
      readTime: '8 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 2,
      category: 'Financial Management',
      title: 'Cash Flow Management: The Lifeline of Your Business',
      description: 'Discover proven strategies to optimize your cash flow, improve working capital, and ensure your business has the liquidity needed to thrive in challenging times.',
      image: 'https://readdy.ai/api/search-image?query=financial%20cash%20flow%20management%20concept%20with%20money%20streams%20flowing%20through%20business%20charts%20and%20graphs%20showing%20positive%20growth%20trends%20with%20professional%20clean%20background&width=800&height=500&seq=res2&orientation=landscape',
      date: 'March 10, 2024',
      readTime: '10 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 3,
      category: 'Leadership',
      title: 'The Role of Interim CXO in Business Transformation',
      description: 'Understand how experienced interim executives can provide immediate leadership, strategic direction, and operational expertise during critical transition periods.',
      image: 'https://readdy.ai/api/search-image?query=confident%20business%20executive%20leader%20in%20modern%20corporate%20office%20environment%20with%20team%20collaboration%20and%20strategic%20planning%20elements%20showing%20professional%20leadership%20with%20clean%20background&width=800&height=500&seq=res3&orientation=landscape',
      date: 'March 5, 2024',
      readTime: '7 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 4,
      category: 'Turnaround',
      title: 'Navigating Chapter 11: A Comprehensive Guide',
      description: 'A detailed overview of the Chapter 11 bankruptcy process, restructuring options, and how to position your business for successful emergence and long-term viability.',
      image: 'https://readdy.ai/api/search-image?query=legal%20business%20restructuring%20concept%20with%20corporate%20documents%20and%20financial%20recovery%20planning%20elements%20showing%20professional%20guidance%20with%20clean%20minimalist%20background&width=800&height=500&seq=res4&orientation=landscape',
      date: 'February 28, 2024',
      readTime: '12 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 5,
      category: 'Case Studies',
      title: 'Manufacturing Company Turnaround: A Success Story',
      description: 'How we helped a struggling manufacturing firm reduce costs by 35%, improve operational efficiency, and return to profitability within 18 months through strategic intervention.',
      image: 'https://readdy.ai/api/search-image?query=successful%20manufacturing%20facility%20with%20modern%20production%20lines%20and%20efficient%20operations%20showing%20business%20recovery%20and%20growth%20with%20clean%20industrial%20background&width=800&height=500&seq=res5&orientation=landscape',
      date: 'February 20, 2024',
      readTime: '9 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 6,
      category: 'Financial Management',
      title: 'Debt Restructuring Strategies for Distressed Companies',
      description: 'Explore effective approaches to negotiate with creditors, restructure debt obligations, and create sustainable payment plans that preserve business operations.',
      image: 'https://readdy.ai/api/search-image?query=financial%20debt%20restructuring%20concept%20with%20business%20documents%20and%20negotiation%20elements%20showing%20strategic%20planning%20and%20recovery%20with%20professional%20clean%20background&width=800&height=500&seq=res6&orientation=landscape',
      date: 'February 15, 2024',
      readTime: '11 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 7,
      category: 'Business Strategy',
      title: 'Building Resilience: Preparing Your Business for Economic Uncertainty',
      description: 'Learn how to create robust contingency plans, diversify revenue streams, and build organizational resilience to weather economic downturns and market volatility.',
      image: 'https://readdy.ai/api/search-image?query=resilient%20business%20strategy%20concept%20with%20strong%20foundation%20and%20growth%20elements%20showing%20economic%20stability%20and%20planning%20with%20clean%20professional%20background&width=800&height=500&seq=res7&orientation=landscape',
      date: 'February 10, 2024',
      readTime: '8 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 8,
      category: 'Leadership',
      title: 'Crisis Leadership: Making Critical Decisions Under Pressure',
      description: 'Master the essential skills and frameworks needed to lead effectively during business crises, make tough decisions quickly, and maintain team morale through challenging periods.',
      image: 'https://readdy.ai/api/search-image?query=crisis%20management%20leadership%20with%20business%20executive%20making%20strategic%20decisions%20in%20professional%20office%20environment%20showing%20confidence%20and%20expertise%20with%20clean%20background&width=800&height=500&seq=res8&orientation=landscape',
      date: 'February 5, 2024',
      readTime: '10 min read',
      link: '/contact',
      featured: false,
    },
    {
      id: 9,
      category: 'Case Studies',
      title: 'Retail Chain Revival: From Bankruptcy to Market Leader',
      description: 'An in-depth case study of how strategic repositioning, operational improvements, and capital restructuring transformed a bankrupt retail chain into an industry success story.',
      image: 'https://readdy.ai/api/search-image?query=successful%20modern%20retail%20store%20with%20customers%20and%20vibrant%20shopping%20environment%20showing%20business%20revival%20and%20growth%20with%20clean%20contemporary%20background&width=800&height=500&seq=res9&orientation=landscape',
      date: 'January 28, 2024',
      readTime: '13 min read',
      link: '/contact',
      featured: false,
    },
  ];

  const filteredResources = selectedCategory === 'All'
    ? resources
    : resources.filter(resource => resource.category === selectedCategory);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar isScrolled={isScrolled} />

        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20business%20knowledge%20and%20resources%20library%20with%20books%20documents%20and%20digital%20learning%20elements%20showing%20expertise%20and%20guidance%20in%20modern%20corporate%20setting%20with%20clean%20minimalist%20background&width=1920&height=500&seq=reshero&orientation=landscape"
            alt="Resources"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Resources & Insights</h1>
            <p className="text-xl text-white/95 max-w-2xl mx-auto">
              Expert guidance, industry insights, and proven strategies to help your business navigate challenges and achieve sustainable growth
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-teal-50 hover:text-teal-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map((resource) => (
                <article
                  key={resource.id}
                  className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer ${resource.featured ? 'ring-2 ring-teal-400' : ''}`}
                >
                  <div className="relative h-56 w-full overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="px-3 py-1 bg-teal-500 text-white text-xs font-medium rounded-full">
                        {resource.category}
                      </span>
                      {resource.featured && (
                        <span className="px-3 py-1 bg-white text-teal-600 text-xs font-semibold rounded-full shadow">
                          ✦ New
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {resource.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {resource.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-3">{resource.description}</p>
                    <Link
                      to={resource.link}
                      className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Read More
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Signup Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-100 rounded-full mb-6">
              <i className="ri-mail-send-line text-teal-600 text-2xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Stay Ahead of the Curve
            </h2>
            <p className="text-lg text-slate-600 mb-10 max-w-xl mx-auto">
              Get notified when we publish new articles, insights, and strategies to help your business thrive. No spam — just valuable content.
            </p>
            <form
              id="newsletter-signup-form"
              data-readdy-form
              className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new URLSearchParams();
                formData.append('email', (form.querySelector('[name="email"]') as HTMLInputElement).value);
                formData.append('first_name', (form.querySelector('[name="first_name"]') as HTMLInputElement).value);
                try {
                  const res = await fetch('https://readdy.ai/api/form/d6ne8jk6fojmejgajqf0', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: formData.toString(),
                  });
                  if (res.ok) {
                    setNewsletterSuccess(true);
                    form.reset();
                  }
                } catch {
                  // silent fail
                }
              }}
            >
              <input
                type="text"
                name="first_name"
                placeholder="Your first name"
                required
                className="flex-1 px-4 py-3 text-sm rounded-md border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                required
                className="flex-1 px-4 py-3 text-sm rounded-md border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-teal-500 text-white text-sm font-semibold rounded-md hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer shadow-md"
              >
                Notify Me
              </button>
            </form>
            {newsletterSuccess && (
              <p className="mt-5 text-sm text-teal-600 font-medium flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-line text-lg"></i>
                You&apos;re subscribed! We&apos;ll notify you when new articles are published.
              </p>
            )}
            <p className="mt-4 text-xs text-slate-400">We respect your privacy. Unsubscribe at any time.</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-500">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-white/95 mb-8 max-w-2xl mx-auto">
              Let our experienced team help you navigate challenges and unlock your company's full potential
            </p>
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-teal-600 text-base font-semibold rounded-full hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                Schedule a Consultation
              </button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
